import { useMemo, useState } from "react";

type Policy = "none" | "quarantine" | "reject";
type Align = "r" | "s";

export function DmarcGenerator() {
  const [policy, setPolicy] = useState<Policy>("none");
  const [subPolicy, setSubPolicy] = useState<Policy | "">("");
  const [pct, setPct] = useState(100);
  const [rua, setRua] = useState("");
  const [ruf, setRuf] = useState("");
  const [adkim, setAdkim] = useState<Align>("r");
  const [aspf, setAspf] = useState<Align>("r");
  const [copied, setCopied] = useState(false);

  const record = useMemo(() => {
    const tags: string[] = ["v=DMARC1", `p=${policy}`];
    if (subPolicy) tags.push(`sp=${subPolicy}`);
    if (pct !== 100) tags.push(`pct=${pct}`);
    const ruaList = parseAddrs(rua);
    if (ruaList) tags.push(`rua=${ruaList}`);
    const rufList = parseAddrs(ruf);
    if (rufList) tags.push(`ruf=${rufList}`);
    if (adkim !== "r") tags.push(`adkim=${adkim}`);
    if (aspf !== "r") tags.push(`aspf=${aspf}`);
    return tags.join("; ") + ";";
  }, [policy, subPolicy, pct, rua, ruf, adkim, aspf]);

  const copy = () => {
    navigator.clipboard?.writeText(record).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <main className="console">
      <div className="input-row">
        <span className="input-row-label">DMARC</span>
        <span className="gen-intro">Build a DMARC policy record, then publish it as a TXT record at <code>_dmarc.&lt;your-domain&gt;</code>.</span>
      </div>

      <div className="gen-grid">
        <section className="gen-form">
          <label className="gen-field">
            <span className="gen-label">Policy <code>p</code></span>
            <select value={policy} onChange={(e) => setPolicy(e.target.value as Policy)} aria-label="DMARC policy">
              <option value="none">none — monitor only (start here)</option>
              <option value="quarantine">quarantine — send failures to spam</option>
              <option value="reject">reject — block failures</option>
            </select>
            <span className="gen-hint">Start at <strong>none</strong> for 2–4 weeks to collect reports, then ratchet up.</span>
          </label>

          <label className="gen-field">
            <span className="gen-label">Subdomain policy <code>sp</code></span>
            <select value={subPolicy} onChange={(e) => setSubPolicy(e.target.value as Policy | "")} aria-label="DMARC subdomain policy">
              <option value="">(inherit from p)</option>
              <option value="none">none</option>
              <option value="quarantine">quarantine</option>
              <option value="reject">reject</option>
            </select>
          </label>

          <label className="gen-field">
            <span className="gen-label">Aggregate reports <code>rua</code></span>
            <input type="text" value={rua} onChange={(e) => setRua(e.target.value)} placeholder="dmarc@example.com" spellCheck={false} autoCapitalize="off" autoCorrect="off" />
            <span className="gen-hint">Where daily XML reports are sent. Comma-separate multiple. Strongly recommended.</span>
          </label>

          <label className="gen-field">
            <span className="gen-label">Forensic reports <code>ruf</code></span>
            <input type="text" value={ruf} onChange={(e) => setRuf(e.target.value)} placeholder="(optional)" spellCheck={false} autoCapitalize="off" autoCorrect="off" />
          </label>

          <label className="gen-field">
            <span className="gen-label">Apply to <code>pct</code></span>
            <input type="number" min={1} max={100} value={pct} onChange={(e) => setPct(clampPct(e.target.value))} aria-label="Percentage of mail" />
            <span className="gen-hint">% of failing mail the policy applies to. Useful for a gradual rollout.</span>
          </label>

          <div className="gen-row2">
            <label className="gen-field">
              <span className="gen-label">DKIM alignment <code>adkim</code></span>
              <select value={adkim} onChange={(e) => setAdkim(e.target.value as Align)} aria-label="DKIM alignment">
                <option value="r">relaxed</option>
                <option value="s">strict</option>
              </select>
            </label>
            <label className="gen-field">
              <span className="gen-label">SPF alignment <code>aspf</code></span>
              <select value={aspf} onChange={(e) => setAspf(e.target.value as Align)} aria-label="SPF alignment">
                <option value="r">relaxed</option>
                <option value="s">strict</option>
              </select>
            </label>
          </div>
        </section>

        <section className="gen-output">
          <div className="gen-out-head">
            <span className="gen-out-label">Your DMARC record</span>
            <button type="button" className="gen-copy" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
          </div>
          <pre className="gen-record"><code>{record}</code></pre>

          <dl className="gen-dns">
            <div><dt>Type</dt><dd>TXT</dd></div>
            <div><dt>Name / Host</dt><dd><code>_dmarc</code></dd></div>
            <div><dt>Value</dt><dd>the record above</dd></div>
            <div><dt>TTL</dt><dd>3600 (1 hour) is fine</dd></div>
          </dl>

          <p className="whois-cross">
            After publishing, verify it propagated:{" "}
            <a href="/dmarc-check">DMARC check</a> · learn more in the{" "}
            <a href="/glossary/dmarc">DMARC glossary</a>.
          </p>
        </section>
      </div>
    </main>
  );
}

// Accepts "a@b.com, c@d.com" → "mailto:a@b.com,mailto:c@d.com". Ignores blanks
// and entries that already include a scheme.
function parseAddrs(raw: string): string | null {
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.includes(":") ? s : `mailto:${s}`));
  return parts.length ? parts.join(",") : null;
}

function clampPct(v: string): number {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 100;
  return Math.min(100, Math.max(1, n));
}
